/**
 * Gráfico de barras horizontais para Documentos por Status
 * Similar ao documentosStatusChart.js do backup
 */
import { useMemo, useEffect, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import { cn } from '@/lib/utils';
import type { StatusCount } from '../hooks';

// Hook para detectar tema dark
function useIsDarkMode() {
  const [isDark, setIsDark] = useState(() => 
    document.documentElement.classList.contains('dark')
  );

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains('dark'));
    });
    
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });
    
    return () => observer.disconnect();
  }, []);

  return isDark;
}

interface DocumentosStatusChartProps {
  data: StatusCount[];
  isLoading?: boolean;
  className?: string;
  totalDocuments?: number;
}

// Cores para cada status de documento
const STATUS_COLORS: Record<string, string> = {
  'Vigente': '#10b981', // emerald-500
  'Vigente/Em renovação': '#22c55e', // green-500
  'Permanente': '#3b82f6', // blue-500
  'Isento': '#06b6d4', // cyan-500
  'Vencido': '#ef4444', // red-500
  'Vencido/Em renovação': '#f97316', // orange-500
  'Inexistente': '#dc2626', // red-600
  'Inexistente/Em obtenção': '#f59e0b', // amber-500
  'Não configurado': '#6b7280', // gray-500
};

function getStatusColor(status: string): string {
  const key = Object.keys(STATUS_COLORS).find(
    (k) => k.toLowerCase() === status.toLowerCase()
  );
  return key ? STATUS_COLORS[key] : '#9ca3af'; // gray-400 default
}

export function DocumentosStatusChart({
  data,
  isLoading = false,
  className,
  totalDocuments,
}: DocumentosStatusChartProps) {
  const isDark = useIsDarkMode();
  const textColor = isDark ? '#e5e7eb' : '#374151'; // gray-200 / gray-700

  const option: EChartsOption = useMemo(() => {
    if (!data || data.length === 0) return {};

    // Ordenar por count decrescente
    const sorted = [...data].sort((a, b) => b.count - a.count);
    
    const categories = sorted.map(d => d.status);
    const values = sorted.map(d => d.count);
    const colors = sorted.map(d => getStatusColor(d.status));

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params: unknown) => {
          const paramsArray = params as Array<{
            name: string;
            marker: string;
            value: number;
          }>;
          if (!paramsArray.length) return '';
          const p = paramsArray[0];
          return `${p.marker} <strong>${p.name}</strong><br/>Documentos: <strong>${p.value.toLocaleString('pt-BR')}</strong>`;
        },
      },
      grid: {
        left: 24,
        right: 12,
        top: 12,
        bottom: 12,
        containLabel: true,
      },
      xAxis: {
        type: 'value',
        axisLabel: {
          formatter: (value: number) => value.toLocaleString('pt-BR'),
          color: textColor,
        },
        splitLine: {
          lineStyle: { color: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' },
        },
      },
      yAxis: {
        type: 'category',
        data: categories,
        inverse: true,
        axisLabel: {
          width: 150,
          overflow: 'truncate',
          ellipsis: '...',
          color: textColor,
        },
      },
      series: [
        {
          type: 'bar',
          data: values.map((v, i) => ({
            value: v,
            itemStyle: { color: colors[i] },
          })),
          label: {
            show: true,
            position: 'right',
            formatter: '{c}',
            fontSize: 11,
            color: textColor,
          },
        },
      ],
    };
  }, [data, textColor, isDark]);

  const evaluatedCount = totalDocuments ?? data?.reduce((sum, item) => sum + item.count, 0) ?? 0;

  if (isLoading) {
    return (
      <div className={cn('bg-card rounded-xl p-4 shadow-sm', className)}>
        <div className="mb-2 flex items-center justify-between gap-2">
          <h3 className="text-sm font-medium text-muted-foreground">
            Documentos por Status
          </h3>
          <span className="text-xs text-muted-foreground">Carregando...</span>
        </div>
        <div className="flex items-center justify-center h-[250px]">
          <div className="animate-pulse text-muted-foreground">Carregando...</div>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className={cn('bg-card rounded-xl p-4 shadow-sm', className)}>
        <div className="mb-2 flex items-center justify-between gap-2">
          <h3 className="text-sm font-medium text-muted-foreground">
            Documentos por Status
          </h3>
          <span className="text-xs text-muted-foreground">0 avaliados</span>
        </div>
        <div className="flex items-center justify-center h-[250px] text-muted-foreground">
          Sem dados disponíveis
        </div>
      </div>
    );
  }

  // Altura fixa para manter consistência com o card de Unidades por Situação
  const chartHeight = 280;

  return (
    <div className={cn('bg-card rounded-xl p-4 shadow-sm h-[380px] flex flex-col', className)}>
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="text-sm font-medium text-muted-foreground">
          Documentos por Status
        </h3>
        <span className="text-xs text-muted-foreground">
          {evaluatedCount.toLocaleString('pt-BR')} documentos avaliados
        </span>
      </div>
      <div className="flex-1">
        <ReactECharts
          option={option}
          style={{ height: `${chartHeight}px`, width: '100%' }}
          opts={{ renderer: 'canvas' }}
        />
      </div>
    </div>
  );
}
